import { db } from '../utils/firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  setDoc, 
  increment, 
  serverTimestamp,
  getDoc 
} from 'firebase/firestore';

/**
 * 세션 ID 생성 및 관리
 */
let sessionId = null;
export const getSessionId = () => {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // 세션 ID를 sessionStorage에 저장 (탭별로 독립적)
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = sessionStorage.getItem('analytics_session_id');
      if (stored) {
        sessionId = stored;
      } else {
        sessionStorage.setItem('analytics_session_id', sessionId);
      }
    }
  }
  return sessionId;
};

/**
 * Firestore에 action_logs 기록
 * @param {object} logData - 로그 데이터
 */
export const writeActionLog = async (logData) => {
  if (!db) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Firestore db not initialized');
    }
    return;
  }

  try {
    await addDoc(collection(db, 'action_logs'), {
      ...logData,
      ts: serverTimestamp(),
      session_id: getSessionId(),
    });
  } catch (error) {
    console.error('[Analytics] Error writing action log:', error);
  }
};

/**
 * 일자별 집계 업데이트
 * @param {string} date - 날짜 (yyyymmdd)
 * @param {object} context - 컨텍스트 정보
 * @param {string} eventType - 'page_view' | 'ui_click'
 * @param {string} uiTargetKey - UI 타겟 키 (ui_click일 때만)
 */
export const updateDailyCounter = async (date, context, eventType, uiTargetKey = null) => {
  if (!db || !context.gc_id) {
    return;
  }

  try {
    const { gc_id, gc_name, page_key, page_path } = context;
    
    if (eventType === 'page_view') {
      // 페이지 뷰 카운터
      // Firestore 문서 참조는 짝수 개의 세그먼트를 가져야 함 (컬렉션-문서-컬렉션-문서...)
      const pageDocRef = doc(
        db,
        'counters_daily',
        date,
        'golf_courses',
        gc_id,
        'pages',
        page_key
      );
      
      const pageDoc = await getDoc(pageDocRef);
      if (!pageDoc.exists()) {
        // 첫 생성 시 메타데이터 설정
        await setDoc(pageDocRef, {
          date,
          gc_id,
          gc_name: gc_name || null,
          page_key,
          page_path: page_path || '',
          event_type: 'page_view',
          count: 0,
          last_updated: serverTimestamp(),
        });
      }
      
      await setDoc(pageDocRef, {
        count: increment(1),
        last_updated: serverTimestamp(),
      }, { merge: true });
      
    } else if (eventType === 'ui_click' && uiTargetKey) {
      // UI 클릭 카운터
      const clickDocRef = doc(
        db,
        'counters_daily',
        date,
        'golf_courses',
        gc_id,
        'pages',
        page_key,
        'ui_targets',
        uiTargetKey
      );
      
      const clickDoc = await getDoc(clickDocRef);
      if (!clickDoc.exists()) {
        // 첫 생성 시 메타데이터 설정
        await setDoc(clickDocRef, {
          date,
          gc_id,
          gc_name: gc_name || null,
          page_key,
          ui_target_key: uiTargetKey,
          event_type: 'ui_click',
          count: 0,
          last_updated: serverTimestamp(),
        });
      }
      
      await setDoc(clickDocRef, {
        count: increment(1),
        last_updated: serverTimestamp(),
      }, { merge: true });
    }
  } catch (error) {
    console.error('[Analytics] Error updating daily counter:', error);
  }
};

/**
 * 누적 집계 업데이트
 * @param {object} context - 컨텍스트 정보
 * @param {string} eventType - 'page_view' | 'ui_click'
 * @param {string} uiTargetKey - UI 타겟 키 (ui_click일 때만)
 */
export const updateTotalCounter = async (context, eventType, uiTargetKey = null) => {
  if (!db || !context.gc_id) {
    return;
  }

  try {
    const { gc_id, gc_name, page_key, page_path } = context;
    
    if (eventType === 'page_view') {
      // 페이지 뷰 누적 카운터
      // Firestore 문서 참조는 짝수 개의 세그먼트를 가져야 함 (컬렉션-문서-컬렉션-문서...)
      // 구조: counters_total/{gc_id}/pages/{page_key} (4개 세그먼트)
      const pageDocRef = doc(
        db,
        'counters_total',
        gc_id,
        'pages',
        page_key
      );
      
      const pageDoc = await getDoc(pageDocRef);
      if (!pageDoc.exists()) {
        await setDoc(pageDocRef, {
          gc_id,
          gc_name: gc_name || null,
          page_key,
          page_path: page_path || '',
          event_type: 'page_view',
          total_count: 0,
          first_seen: serverTimestamp(),
          last_updated: serverTimestamp(),
        });
      }
      
      await setDoc(pageDocRef, {
        total_count: increment(1),
        last_updated: serverTimestamp(),
      }, { merge: true });
      
    } else if (eventType === 'ui_click' && uiTargetKey) {
      // UI 클릭 누적 카운터
      // 구조: counters_total/{gc_id}/pages/{page_key}/ui_targets/{uiTargetKey} (6개 세그먼트)
      const clickDocRef = doc(
        db,
        'counters_total',
        gc_id,
        'pages',
        page_key,
        'ui_targets',
        uiTargetKey
      );
      
      const clickDoc = await getDoc(clickDocRef);
      if (!clickDoc.exists()) {
        await setDoc(clickDocRef, {
          gc_id,
          gc_name: gc_name || null,
          page_key,
          ui_target_key: uiTargetKey,
          event_type: 'ui_click',
          total_count: 0,
          first_seen: serverTimestamp(),
          last_updated: serverTimestamp(),
        });
      }
      
      await setDoc(clickDocRef, {
        total_count: increment(1),
        last_updated: serverTimestamp(),
      }, { merge: true });
    }
  } catch (error) {
    console.error('[Analytics] Error updating total counter:', error);
  }
};
